import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Star, MapPin, Phone, Mail, MessageCircle, Award, CheckCircle, Play, X } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Breadcrumbs from '../components/Breadcrumbs';
import { serviceProviders } from '../data/serviceProviders';

const ServiceProviderDetailPage = () => {
  const { country, providerId } = useParams();
  const navigate = useNavigate();
  const [provider, setProvider] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    const found = serviceProviders.find(p => p.id === providerId);
    setProvider(found);
  }, [providerId]);

  if (!provider) {
    return (
      <div className="min-h-screen bg-aug-cream">
        <Header />
        <div className="pt-24 pb-16 text-center">
          <h1 className="text-2xl font-bold text-aug-gray-dark">Prestador não encontrado</h1>
        </div>
      </div>
    );
  }

  const handleContactWhatsApp = () => {
    if (provider.whatsapp) {
      window.open(`https://wa.me/${provider.whatsapp}`, '_blank');
    }
  };

  const handleRequestQuote = () => {
    navigate(`/${country}/crm?provider=${provider.id}`);
  };

  return (
    <div className="min-h-screen bg-aug-cream">
      <Header />

      <div className="pt-24 pb-16">
        <div className="max-w-[1400px] mx-auto px-xl">
          <Breadcrumbs
            items={[
              { label: 'Home', path: `/${country}` },
              { label: 'Serviços', path: `/${country}/services` },
              { label: provider.name, path: `/${country}/services/${provider.id}` }
            ]}
          />

          {/* Header do Perfil */}
          <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
            <div className="flex flex-col md:flex-row gap-6">
              {/* Profile Image */}
              <div className="flex-shrink-0">
                <img
                  src={provider.profile_image}
                  alt={provider.name}
                  className="w-32 h-32 rounded-xl object-cover shadow-md"
                />
              </div>

              {/* Info */}
              <div className="flex-1">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h1 className="text-3xl font-bold text-aug-gray-dark mb-1">
                      {provider.name}
                    </h1>
                    <p className="text-lg" style={{ color: '#1B5E20' }}>
                      {provider.category_name}
                    </p>
                  </div>
                  {provider.verified && (
                    <div className="flex items-center gap-1 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-medium">
                      <CheckCircle className="w-4 h-4" />
                      Verificado
                    </div>
                  )}
                </div>

                <p className="text-aug-gray-medium mb-4">
                  {provider.description}
                </p>

                {/* Stats */}
                <div className="flex flex-wrap gap-6 mb-4">
                  <div className="flex items-center gap-2">
                    <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    <div>
                      <span className="text-2xl font-bold text-aug-gray-dark">{provider.average_rating}</span>
                      <span className="text-sm text-aug-gray-medium ml-1">({provider.reviews_count} avaliações)</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Award className="w-5 h-5 text-aug-blue" />
                    <div>
                      <span className="text-2xl font-bold text-aug-gray-dark">{provider.total_contracts}</span>
                      <span className="text-sm text-aug-gray-medium ml-1">contratações</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-aug-blue" />
                    <span className="text-lg text-aug-gray-dark">{provider.city} - {provider.state}</span>
                  </div>
                </div>

                {/* Contact Buttons */}
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={handleRequestQuote}
                    className="px-6 py-3 text-white rounded-lg font-semibold transition-colors shadow-md"
                    style={{ backgroundColor: '#1B5E20' }}
                  >
                    Solicitar Orçamento
                  </button>
                  {provider.whatsapp && (
                    <button
                      onClick={handleContactWhatsApp}
                      className="flex items-center gap-2 px-6 py-3 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600 transition-colors shadow-md"
                    >
                      <MessageCircle className="w-5 h-5" />
                      WhatsApp
                    </button>
                  )}
                  {provider.phone && (
                    <button
                      onClick={() => window.location.href = `tel:${provider.phone}`}
                      className="flex items-center gap-2 px-6 py-3 border-2 border-aug-blue text-aug-blue rounded-lg font-semibold hover:bg-aug-blue hover:text-white transition-colors"
                    >
                      <Phone className="w-5 h-5" />
                      {provider.phone}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Vídeos Demonstrativos */}
          {provider.videos.length > 0 && (
            <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
              <h2 className="text-2xl font-bold text-aug-gray-dark mb-6">
                Vídeos Demonstrativos
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {provider.videos.map((videoUrl, index) => (
                  <div
                    key={index}
                    onClick={() => setSelectedVideo(videoUrl)}
                    className="relative h-48 bg-gray-100 rounded-xl overflow-hidden cursor-pointer group"
                  >
                    <div className="absolute inset-0 bg-black bg-opacity-40 group-hover:bg-opacity-60 transition-all flex items-center justify-center">
                      <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Play className="w-8 h-8 text-aug-blue" />
                      </div>
                    </div>
                    <img
                      src={`https://via.placeholder.com/400x300/1B5E20/ffffff?text=Vídeo ${index + 1}`}
                      alt={`Vídeo ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Avaliações */}
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <h2 className="text-2xl font-bold text-aug-gray-dark mb-6">
              Avaliações de Clientes
            </h2>
            
            {/* Mock Reviews */}
            <div className="space-y-6">
              {[1, 2, 3].map((_, index) => (
                <div key={index} className="border-b border-aug-gray-light pb-6 last:border-0">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h4 className="font-semibold text-aug-gray-dark">Cliente {index + 1}</h4>
                      <div className="flex items-center gap-1 mt-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className="w-4 h-4 fill-yellow-400 text-yellow-400"
                          />
                        ))}
                      </div>
                    </div>
                    <span className="text-sm text-aug-gray-medium">Há 2 semanas</span>
                  </div>
                  <p className="text-aug-gray-medium">
                    Excelente profissional! Trabalho bem feito e pontual. Recomendo!
                  </p>
                  <div className="mt-2 inline-block bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-medium">
                    ✓ Contratação Verificada
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Video Modal */}
      {selectedVideo && (
        <div className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-4xl">
            <button
              onClick={() => setSelectedVideo(null)}
              className="absolute -top-12 right-0 text-white hover:text-aug-gold transition-colors"
            >
              <X className="w-8 h-8" />
            </button>
            <div className="aspect-video bg-black rounded-lg overflow-hidden">
              <iframe
                src={selectedVideo}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ServiceProviderDetailPage;
